import { z } from 'zod';
import { ContactRoleSchema, type ContactRole } from '@shared/product-usage/wire';
import { ROLE_LABEL } from '@shared/product-usage/roles';

const UsageRolesSchema = z.array(ContactRoleSchema);

/**
 * Re-exported from the shared rule module, where the filter picker's field
 * labels (`USAGE_ROLE_FILTER_FIELDS`) read the same table, so a chip and the
 * filter that selects it can never say two different things.
 */
export { ROLE_LABEL };

/**
 * Trust-boundary parse for a `usageRoles` value read off a response that
 * validateResponse may have handed back unvalidated (it warns instead of
 * throwing). Anything that is not exactly an array of known roles becomes []
 * rather than a partial list, so the chip strip never renders from a shape
 * the server did not promise.
 */
export function coerceUsageRoles(value: unknown): ContactRole[] {
  const parsed = UsageRolesSchema.safeParse(value);
  return parsed.success ? parsed.data : [];
}

/**
 * Role chips. A contact commonly holds several ("Champion · Key builder"), so all of them
 * render, in the order the read path returned (most significant first).
 *
 * `compact` is for a table row column with no room for redundancy: a key builder is a
 * builder, so 'builder' is dropped when 'key_builder' is present. Champion is never dropped.
 */
const ROLE_TONE: Record<ContactRole, string> = {
  champion: 'bg-bg-purple-light text-content-action',
  key_builder: 'bg-bg-green-light text-content-success',
  builder: 'bg-bg-tertiary text-content-tertiary',
};

export function RoleChips({ roles, compact = false }: { roles: ContactRole[]; compact?: boolean }) {
  const shown =
    compact && roles.includes('key_builder') ? roles.filter((role) => role !== 'builder') : roles;
  if (shown.length === 0) return <span className="text-xs text-icon-subtle">—</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {shown.map((role) => (
        <span key={role} className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${ROLE_TONE[role]}`}>
          {ROLE_LABEL[role]}
        </span>
      ))}
    </span>
  );
}
